
import { defineComponent } from "vue";
import router from "@/router";
import store from "@/store";

import EnrollmentsService from "@/api/Enrollment/IndividualEnrollmentService";
import StudentsService from "@/api/Aula/StudentsService";
import HeadquartersService from "@/api/Configuration/HeadquartersService";
import AcademicYearService from "@/api/Configuration/AcademicYearService";
import CourseGroupsService from "@/api/Aula/CourseGroupsService";
import CoursesService from "@/api/Aula/CoursesSevice";
import SystemAcademicAssignmentsService from "@/api/Academic/SystemAcademicAssignmentsService";

import StudentAttendanceSessionsService from "@/api/Aula/StudentAttendanceSessionsService";
import StudentAttendanceDetailsService from "@/api/Aula/StudentAttendanceDetailsService";

import type {
  IIndividualEnrollmentModel,
  ICoursesModel,
  IAcademicYearModel,
  IStudentsModel,
  IHeadquartersModel,
  ICourseGroupsModel,
  ISystemAcademicAssignmentsModel,
} from "@/interfaces/modules";
import { SHOW_PRESCHOOL } from "@/enums";

export default defineComponent({
  name: "StudentAttendanceList",
  data() {
    return {
      table: {
        loading: false,
        records: [] as IIndividualEnrollmentModel[],
      } as any,

      academicYears: [] as IAcademicYearModel[],
      currentAcademicYearId: null as number | null,
      currentAcademicYearLabel: "" as string,

      academicAssignments: [] as ISystemAcademicAssignmentsModel[],
      expandedTeachers: {} as Record<string, boolean>,
      expandedSubjects: {} as Record<string, boolean>,

      // Id de sesión (si viene por ruta)
      sessionId: 0,
      // Detalles existentes en BD para esa sesión
      attendanceDetails: [] as any[],

      form: {
        loading: false,
        dialog: false,
        summary: {
          groupName: "",
          headquarterName: "",
          academicYearName: "",
          sessionDate: "",
          studentsCount: 0,
          subjectName: "",
          teacherName: "",
          teacherId: 0,
        } as {
          groupName: string;
          headquarterName: string;
          academicYearName: string;
          sessionDate: string;
          studentsCount: number;
          subjectName: string;
          teacherName: string;
          teacherId: number;
        },
      },

      currentGroupId: null as number | null,
      currentAssignmentId: null as number | null,
      currentItems: [] as any[],

      filters: {
        studentId: null as number | null,
        courseGroupId: null as number | null,
        teacherId: null as number | null,
        subjectId: null as number | null,
      },

      students: [] as IStudentsModel[],
      headquarters: [] as IHeadquartersModel[],
      courseGroups: [] as ICourseGroupsModel[],
      courses: [] as ICoursesModel[],

      attendanceStatusOptions: [
        { value: "PRESENTE", text: "Presente" },
        { value: "AUSENTE", text: "Ausente" },
        { value: "TARDE", text: "Tarde" },
      ],

      groupTableHeaders: [
        {
          key: "StudentName",
          label: "Estudiante",
          sortable: true,
          thClass: "py-0",
          tdClass: "py-0",
        },
        {
          key: "StatusCode",
          label: "Asistencia",
          sortable: false,
          thClass: "py-0",
          tdClass: "py-0",
        },
        {
          key: "IsJustified",
          label: "Justificada",
          sortable: false,
          thClass: "py-0 text-center",
          tdClass: "py-0 text-center",
        },
        {
          key: "JustificationNote",
          label: "Observaciones",
          sortable: false,
          thClass: "py-0",
          tdClass: "py-0",
        },
      ] as any[],
    };
  },
  computed: {
    studentOptions(): Array<{ value: number; text: string }> {
      return this.students
        .map((s) => ({
          value: s.Id,
          text: `${s.IdentificationNumber || ""} - ${s.Name} ${s.LastName || ""
            }`.trim(),
        }))
        .sort((a, b) => a.text.localeCompare(b.text));
    },

    groupOptions(): Array<{ value: number; text: string }> {
      const yearId = this.currentAcademicYearId;
      return this.courseGroups
        .filter((g) =>
          yearId ? Number(g.AcademicYearId) === Number(yearId) : true
        )
        .map((g) => ({ value: g.Id, text: g.Name }))
        .sort((a, b) => a.text.localeCompare(b.text));
    },

    teacherOptions(): Array<{ value: number; text: string }> {
      const map = new Map<number, string>();
      const cgFilter = this.filters.courseGroupId;

      for (const a of this.academicAssignments as any[]) {
        const teacherId = (a as any).TeacherId;
        if (!teacherId) continue;
        const gid = Number(a.CourseGroupId || 0);
        if (cgFilter && Number(gid) !== Number(cgFilter)) continue;

        if (!map.has(Number(teacherId))) {
          map.set(Number(teacherId), a.TeacherName || `Docente ${teacherId}`);
        }
      }

      return Array.from(map.entries())
        .map(([value, text]) => ({ value, text }))
        .sort((a, b) => a.text.localeCompare(b.text));
    },

    subjectOptions(): Array<{ value: number; text: string }> {
      const map = new Map<number, string>();
      const cgFilter = this.filters.courseGroupId;
      const teacherFilter = this.filters.teacherId;

      for (const a of this.academicAssignments as any[]) {
        const subjId = Number(a.SubjectId || 0);
        if (!subjId) continue;

        const gid = Number(a.CourseGroupId || 0);
        if (cgFilter && Number(gid) !== Number(cgFilter)) continue;

        const teacherId = (a as any).TeacherId;
        if (teacherFilter && Number(teacherId || 0) !== Number(teacherFilter))
          continue;

        if (!map.has(subjId)) {
          map.set(subjId, a.SubjectName || `Asignatura ${subjId}`);
        }
      }

      return Array.from(map.entries())
        .map(([value, text]) => ({ value, text }))
        .sort((a, b) => a.text.localeCompare(b.text));
    },

    // Enrolments filtrados (por año, grupo, estudiante)
    filteredItems(): IIndividualEnrollmentModel[] {
      return (this.table.records as any[]).filter((it) => {
        const stOk =
          this.filters.studentId == null ||
          Number(it.StudentId) === Number(this.filters.studentId);

        const cgOk =
          this.filters.courseGroupId == null ||
          Number(it.CourseGroupId) === Number(this.filters.courseGroupId);

        const yearOk =
          !this.currentAcademicYearId ||
          Number(it.AcademicYearId) === Number(this.currentAcademicYearId);

        const statusNorm = this.normalizeStatus(it.Status);
        const isMatriculado =
          statusNorm === "MATRICULADO" || statusNorm === "MATRICULA";

        return stOk && cgOk && yearOk && isMatriculado;
      });
    },

    // TREE: Grupo -> Profesor -> Materias
    groupsForTree(): Array<{
      groupId: number;
      groupName: string;
      courseName: string;
      headquarterName: string;
      academicYearNameDisplay: string;
      items: any[];
      counts: { total: number; enrolled: number; unenrolled: number };
      teachers: Array<{
        teacherKey: string; // TeacherId as string
        teacherLabel: string;
        subjects: Array<{
          assignmentId: number;
          subjectId: number;
          subjectName: string;
          items: any[];
        }>;
      }>;
    }> {
      const norm = (s?: string) => (s || "").trim().toUpperCase();

      // 1) Agrupar enrolments por grupo
      const enrollByGroup = new Map<number, any[]>();
      for (const it of this.filteredItems as any[]) {
        const gid = Number(it.CourseGroupId || 0);
        if (!gid) continue;
        if (!enrollByGroup.has(gid)) enrollByGroup.set(gid, []);
        enrollByGroup.get(gid)!.push(it);
      }

      // 2) Construir nodos Grupo
      const groupNodes: Map<
        number,
        {
          groupId: number;
          groupName: string;
          courseName: string;
          headquarterName: string;
          academicYearNameDisplay: string;
          items: any[];
          teachersMap: Map<
            string,
            {
              teacherKey: string; // TeacherId as string
              teacherLabel: string;
              subjectsMap: Map<
                number,
                {
                  assignmentId: number;
                  subjectId: number;
                  subjectName: string;
                  items: any[];
                }
              >;
            }
          >;
        }
      > = new Map();

      const yearFilter = this.currentAcademicYearId;
      const cgFilter = this.filters.courseGroupId;
      const teacherFilter = this.filters.teacherId;
      const subjectFilter = this.filters.subjectId;

      for (const a of this.academicAssignments as any[]) {
        const gid = Number(a.CourseGroupId || 0);
        if (!gid) continue;

        const cg = this.courseGroups.find(
          (g) => Number(g.Id) === gid
        ) as any;
        if (!cg) continue;

        const course = this.courses.find(
          (c) => Number(c.Id) === Number(cg.CourseId)
        ) as any;
        const yearId = cg.AcademicYearId || course?.AcademicYearId || null;
        const headquarterId = course?.HeadquarterId ?? null;

        // filtros básicos
        if (yearFilter && Number(yearId) !== Number(yearFilter)) continue;
        if (cgFilter && Number(gid) !== Number(cgFilter)) continue;

        const teacherIdRaw = (a as any).TeacherId;
        if (teacherFilter && Number(teacherIdRaw || 0) !== Number(teacherFilter))
          continue;

        const subjId = Number(a.SubjectId || 0);
        if (!subjId) continue;
        if (subjectFilter && subjId !== Number(subjectFilter)) continue;

        const groupEnrollments = enrollByGroup.get(gid) || [];
        if (!groupEnrollments.length) continue;

        if (!groupNodes.has(gid)) {
          const yearObj = this.academicYears.find(
            (y) => Number((y as any).Id) === Number(yearId)
          ) as any;

          const academicYearNameDisplay =
            yearObj?.Year != null
              ? `Año ${yearObj.Year}`
              : a.AcademicYearName || "";

          const headquarterName =
            headquarterId != null
              ? this.headquarters.find(
                (h) => Number((h as any).Id) === Number(headquarterId)
              )?.Description || ""
              : "";

          groupNodes.set(gid, {
            groupId: gid,
            groupName: cg.Name || `Grupo ${gid}`,
            courseName: course?.Name || a.CourseName || "",
            headquarterName,
            academicYearNameDisplay: academicYearNameDisplay || "N/D",
            items: groupEnrollments,
            teachersMap: new Map(),
          });
        }

        const gNode = groupNodes.get(gid)!;

        // Profesor
        const teacherKey =
          teacherIdRaw != null
            ? String(teacherIdRaw)
            : a.TeacherName || `T-${a.Id}`;
        const teacherLabel = a.TeacherName || `Docente ${teacherKey}`;

        if (!gNode.teachersMap.has(teacherKey)) {
          gNode.teachersMap.set(teacherKey, {
            teacherKey,
            teacherLabel,
            subjectsMap: new Map(),
          });
        }

        const tNode = gNode.teachersMap.get(teacherKey)!;

        // Materia
        if (!tNode.subjectsMap.has(subjId)) {
          const subjectItems = (groupEnrollments || []).map((enr: any) => {
            // Buscar detalle de asistencia (si estamos editando una sesión existente)
            let detail: any = null;
            if (this.sessionId && this.attendanceDetails?.length) {
              detail = this.attendanceDetails.find(
                (d: any) => Number(d.StudentId) === Number(enr.StudentId)
              );
            }

            // Base: datos de matrícula
            const merged: any = {
              ...enr,              // 👈 primero copiamos el enrollment completo
              EnrollmentId: enr.Id // guardamos el Id de matrícula por separado
            };

            if (detail) {
              // Si hay detalle, SUS datos mandan (incluyendo el Id)
              merged.Id = detail.Id;                               // 👈 Id correcto del detalle
              merged.AttendanceSessionId = detail.AttendanceSessionId || this.sessionId || 0;
              merged.StatusCode = detail.StatusCode || "PRESENTE";
              merged.IsJustified = typeof detail.IsJustified === "boolean"
                ? detail.IsJustified
                : false;
              merged.JustificationNote = detail.JustificationNote || "";
            } else {
              // No hay detalle aún -> nuevo registro
              merged.Id = 0;
              merged.AttendanceSessionId = this.sessionId || 0;
              merged.StatusCode = enr.StatusCode || "PRESENTE";
              merged.IsJustified =
                typeof enr.IsJustified === "boolean" ? enr.IsJustified : false;
              merged.JustificationNote = enr.JustificationNote || "";
            }

            return merged;
          });

          tNode.subjectsMap.set(subjId, {
            assignmentId: a.Id,
            subjectId: subjId,
            subjectName: a.SubjectName || `Asignatura ${subjId}`,
            items: subjectItems,
          });
        }

      }

      // 3) Convertir a arreglo final
      const result: any[] = [];
      for (const g of groupNodes.values()) {
        const total = g.items.length;
        const enrolled = g.items.filter(
          (x: any) => norm(x.Status) === "MATRICULADO"
        ).length;
        const unenrolledStrict = g.items.filter(
          (x: any) => norm(x.Status) === "SIN MATRICULAR"
        ).length;
        const unenrolled = unenrolledStrict || Math.max(total - enrolled, 0);

        const teacherEntries = Array.from(g.teachersMap.values()).sort((a, b) =>
          (a.teacherLabel || "").localeCompare(b.teacherLabel || "")
        );

        const teachers = teacherEntries.map((t) => ({
          teacherKey: t.teacherKey, // TeacherId string
          teacherLabel: t.teacherLabel,
          subjects: Array.from(t.subjectsMap.values()).sort((a, b) =>
            (a.subjectName || "").localeCompare(b.subjectName || "")
          ),
        }));

        if (!teachers.length) continue;

        result.push({
          groupId: g.groupId,
          groupName: g.groupName,
          courseName: g.courseName,
          headquarterName: g.headquarterName,
          academicYearNameDisplay: g.academicYearNameDisplay,
          items: g.items,
          counts: { total, enrolled, unenrolled },
          teachers,
        });
      }

      result.sort(
        (a, b) =>
          (a.headquarterName || "").localeCompare(b.headquarterName || "") ||
          (a.groupName || "").localeCompare(b.groupName || "") ||
          a.groupId - b.groupId
      );

      return result;
    },
  },
  async mounted() {
    const idParam = this.$route.params.Id;
    this.sessionId = idParam ? Number(idParam) : 0;

    await this.load();

    if (this.sessionId) {
      await this.loadSessionFromDb(this.sessionId);
    }
  },
  methods: {
    normalizeStatus(s?: string) {
      return (s || "").trim().toUpperCase();
    },

    notify(
      text: string,
      type: "success" | "danger" | "warning" | "info" | "secondary"
    ) {
      const typeMap: any = {
        success: "success",
        danger: "error",
        warning: "warning",
        info: "info",
        secondary: "info",
      };
      store.dispatch("core/popupDialog", {
        text,
        type: typeMap[type] ?? "info",
        showCancelButton: false,
      });
    },

    resolveCurrentAcademicYear() {
      if (!this.academicYears?.length) {
        this.currentAcademicYearId = null;
        this.currentAcademicYearLabel = "";
        return;
      }

      const today = new Date();
      const todayStr = today.toISOString().substring(0, 10);

      let current =
        this.academicYears.find(
          (y: any) => (y as any).IsCurrent === true
        ) || null;

      if (!current) {
        current =
          this.academicYears.find((y) => {
            const start = (y.StartDate || "").substring(0, 10);
            const end = (y.EndDate || "").substring(0, 10);
            return start <= todayStr && end >= todayStr;
          }) || null;
      }

      if (!current) {
        current = [...this.academicYears].sort(
          (a, b) => Number(b.Year) - Number(a.Year)
        )[0];
      }

      if (current) {
        this.currentAcademicYearId = current.Id;
        this.currentAcademicYearLabel = `Año ${current.Year} (${current.StartDate.substring(0, 10)
          } a ${current.EndDate.substring(0, 10)})`;
      } else {
        this.currentAcademicYearId = null;
        this.currentAcademicYearLabel = "";
      }
    },

    teacherKeyId(groupId: number, teacherKey: string): string {
      return `${groupId}::${teacherKey}`;
    },

    toggleTeacher(groupId: number, teacherKey: string) {
      const key = this.teacherKeyId(groupId, teacherKey);
      this.expandedTeachers[key] = !this.expandedTeachers[key];
    },

    isTeacherExpanded(groupId: number, teacherKey: string): boolean {
      const key = this.teacherKeyId(groupId, teacherKey);
      return !!this.expandedTeachers[key];
    },

    subjectKeyId(
      groupId: number,
      teacherKey: string,
      assignmentId: number
    ): string {
      return `${groupId}::${teacherKey}::${assignmentId}`;
    },

    toggleSubject(groupId: number, teacherKey: string, assignmentId: number) {
      const key = this.subjectKeyId(groupId, teacherKey, assignmentId);
      this.expandedSubjects[key] = !this.isSubjectExpanded(
        groupId,
        teacherKey,
        assignmentId
      );
    },

    isSubjectExpanded(
      groupId: number,
      teacherKey: string,
      assignmentId: number
    ): boolean {
      const key = this.subjectKeyId(groupId, teacherKey, assignmentId);
      if (!(key in this.expandedSubjects)) return true;
      return !!this.expandedSubjects[key];
    },

    async load() {
      this.table.loading = true;
      try {
        const [years, courses] = await Promise.all([
          AcademicYearService.GetAcademicYears(),
          CoursesService.GetCourses(SHOW_PRESCHOOL.No),
        ]);
        this.academicYears = years || [];
        this.courses = courses || [];

        this.resolveCurrentAcademicYear();

        const [records, students, headquarters, groups, assignments] =
          await Promise.all([
            EnrollmentsService.GetEnrollments(),
            StudentsService.GetStudents(),
            HeadquartersService.GetHeadquarters(),
            CourseGroupsService.GetCourseGroups(),
            SystemAcademicAssignmentsService.GetAssignments(),
          ]);

        const yearId = this.currentAcademicYearId;

        this.table.records = (records || []).filter((r: any) =>
          yearId ? Number(r.AcademicYearId) === Number(yearId) : true
        );

        this.courseGroups = (groups || []).filter((g: any) =>
          yearId ? Number(g.AcademicYearId) === Number(yearId) : true
        );

        this.students = students || [];
        this.headquarters = headquarters || [];
        this.academicAssignments = assignments || [];
      } catch (err) {
        console.error(err);
        this.notify(
          "Error cargando la información para la lista de asistencia.",
          "danger"
        );
      } finally {
        this.table.loading = false;
      }
    },

    async loadSessionFromDb(id: number) {
      try {
        const header = await StudentAttendanceSessionsService.GetById(id);
        if (!header) {
          this.notify("No se encontró la sesión de asistencia.", "warning");
          return;
        }

        if ((header as any).CourseGroupId) {
          this.filters.courseGroupId = Number((header as any).CourseGroupId);
        }
        if ((header as any).TeacherId) {
          this.filters.teacherId = Number((header as any).TeacherId);
        }
        if ((header as any).SubjectId) {
          this.filters.subjectId = Number((header as any).SubjectId);
        }

        const details =
          (await StudentAttendanceDetailsService.GetBySessionId?.(id)) || [];
        this.attendanceDetails = details;
      } catch (err) {
        console.error(err);
        this.notify(
          "Error cargando la sesión de asistencia desde la base de datos.",
          "danger"
        );
      }
    },

    onFiltersChange() {
      // computed se recalcula solo
    },

    editStudent(id: number) {
      router.push(`/aula/student-detail/${id}`);
    },

    async handleSubmit(e: Event) {
      e.preventDefault();
      if (this.form.loading) return;

      if (!this.currentGroupId || !this.currentAssignmentId) {
        this.notify(
          "No se encontró el grupo o la asignación seleccionada.",
          "danger"
        );
        return;
      }

      if (!this.currentItems || !this.currentItems.length) {
        this.notify(
          "No se encontraron estudiantes para registrar asistencia.",
          "danger"
        );
        return;
      }

      const first: any = this.currentItems[0];
      const sessionDate = this.form.summary.sessionDate;
      if (!sessionDate) {
        this.notify("No se pudo determinar la fecha de asistencia.", "danger");
        return;
      }

      const assignment = this.academicAssignments.find(
        (a) => a.Id === this.currentAssignmentId
      ) as any;

      const sessionPayload: any = {
        Id: this.sessionId ? this.sessionId : 0,
        AcademicYearId: first.AcademicYearId ?? null,
        Date: sessionDate,
        CourseGroupId: first.CourseGroupId,
        SubjectId: assignment?.SubjectId ?? null,
        TeacherId: assignment?.TeacherId ?? null,
        PeriodId: assignment?.PeriodId ?? null,
        Comments: "Llamado de asistencia registrado desde el sistema " + sessionDate,
      };


      const detailsPayloads = this.currentItems.map((it: any) => ({
        Id: it.Id, // <-- ahora es el Id del detalle de asistencia
        AttendanceSessionId: it.AttendanceSessionId || 0,
        StudentId: it.StudentId,
        StatusCode: it.StatusCode || "AUSENTE",
        IsJustified: !!it.IsJustified,
        JustificationNote: it.JustificationNote || "",
        MinutesPresent: null,
        Comments: "",
      }));

      if (!detailsPayloads.length) {
        this.notify(
          "No hay estudiantes en el grupo para registrar asistencia.",
          "warning"
        );
        return;
      }

      this.form.loading = true;
      try {
        let createdSession: any = {};
        if (!this.sessionId) {
          createdSession = (await StudentAttendanceSessionsService.Post(
            sessionPayload
          )) as any;
        }

        for (const detail of detailsPayloads) {
          detail.AttendanceSessionId = this.sessionId
            ? this.sessionId
            : createdSession.Id;
          await StudentAttendanceDetailsService.Post(detail);
        }

        this.notify(
          `Asistencia registrada correctamente para ${detailsPayloads.length} estudiante(s).`,
          "success"
        );
        this.form.dialog = false;
        this.currentGroupId = null;
        this.currentAssignmentId = null;
        this.currentItems = [];

        router.push(`/aula/student-attendance`);
      } catch (err) {
        console.error(err);
        this.notify(
          "Ocurrió un error al registrar la asistencia del grupo.",
          "danger"
        );
      } finally {
        this.form.loading = false;
      }
    },

    openDialog(grp: any, tch: any, sub: any) {
      this.currentGroupId = grp.groupId;
      this.currentAssignmentId = sub.assignmentId;
      this.currentItems = sub.items || [];

      if (!this.currentItems.length) {
        this.notify(
          "No hay estudiantes en este grupo para registrar asistencia.",
          "warning"
        );
        return;
      }

      const today = new Date();
      const yyyy = today.getFullYear();
      const mm = String(today.getMonth() + 1).padStart(2, "0");
      const dd = String(today.getDate()).padStart(2, "0");
      const todayStr = `${yyyy}-${mm}-${dd}`;

      this.form.summary = {
        groupName: grp.groupName,
        headquarterName: grp.headquarterName,
        academicYearName: grp.academicYearNameDisplay,
        sessionDate: todayStr,
        studentsCount: this.currentItems.length,
        subjectName: sub.subjectName || "",
        teacherName: tch.teacherLabel || "",
        teacherId: Number(tch.teacherKey) || 0,
      };

      this.form.dialog = true;
    },
  },
});
